import { Typography, Accordion, AccordionSummary, AccordionDetails, Box } from '@mui/material';
import { useState, useEffect, FC } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import dayjs from 'dayjs';
import clsx from 'clsx';
import './PhaseAccordionItem.scss';
import { EnergyFeedback } from '../../../../../../Energy';
import { PhaseEmaTimeline } from '../../../../../../../data-access';
import { CircadianPhase, EnergyFeedbackType } from '@demind-inc/core';
import { useTranslation } from 'react-i18next';
import { getCircadianPhaseTitle } from '../../../../../i18n';

interface PhaseAccordionItemProps {
  phaseData: PhaseEmaTimeline;
  expanded: string | false;
  onExpandedChange: (expanded: CircadianPhase | false) => void;
  timeFormatString: string;
  handleUpdatePhaseEma: (phase: CircadianPhase, feedbackType: EnergyFeedbackType) => void;
}

const PhaseAccordionItem: FC<PhaseAccordionItemProps> = ({
  phaseData,
  expanded,
  onExpandedChange,
  timeFormatString,
  handleUpdatePhaseEma,
}) => {
  const { t } = useTranslation();
  const [selectedFeedback, setSelectedFeedback] = useState<EnergyFeedbackType | undefined>(
    undefined
  );
  const futurePhase = dayjs().isBefore(dayjs(phaseData.startTime));

  useEffect(() => {
    setSelectedFeedback(phaseData.energy);
  }, [phaseData.energy]);

  const handleFeedbackSelect = async (feedbackType: EnergyFeedbackType) => {
    setSelectedFeedback(feedbackType);
    if (typeof handleUpdatePhaseEma === 'function') {
      await handleUpdatePhaseEma(phaseData.phase, feedbackType);
    }
  };

  const handleAccordionChange = (panel: CircadianPhase) => (_, isExpanded: boolean) => {
    if (!futurePhase) {
      onExpandedChange(isExpanded ? panel : false);
    }
  };

  const startTime = dayjs(phaseData.startTime).format(timeFormatString);

  return (
    <Accordion
      key={phaseData.phase}
      expanded={expanded === phaseData.phase}
      onChange={handleAccordionChange(phaseData.phase)}
      className={clsx('phase-accordion-item', {
        'phase-accordion-item--future': futurePhase,
      })}
      sx={{
        background: 'none',
        border: 'none',
        borderRadius: '12px',
        marginBottom: '0px',
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '0px',
        },
      }}
    >
      <AccordionSummary
        className={clsx('phase-accordion-item__summary', {
          'phase-accordion-item__summary--future': futurePhase,
        })}
        sx={{ position: 'relative' }}
      >
        <Box className="phase-accordion-item__collapsed-row">
          <Box className="phase-accordion-item__collapsed-time-group">
            <Typography
              className={clsx('phase-accordion-item__collapsed-time', {
                'phase-accordion-item__collapsed-time--future': futurePhase,
              })}
              variant="subtitle2"
            >
              {startTime}
            </Typography>
            <Box
              className={clsx('phase-accordion-item__collapsed-dashed-line', {
                'phase-accordion-item__collapsed-dashed-line--future': futurePhase,
              })}
            />
          </Box>
          <Typography
            className={clsx('phase-accordion-item__collapsed-title', {
              'phase-accordion-item__collapsed-title--future': futurePhase,
            })}
            variant="body1"
          >
            {getCircadianPhaseTitle(t, phaseData.phase, phaseData.title)}
          </Typography>
        </Box>
        <Box
          className="phase-accordion-item__custom-expand-icon"
          sx={{
            position: 'absolute',
            top: 1,
            right: 1,
            display: 'flex',
            alignItems: 'center',
            transition: 'transform 0.2s',
            transform: expanded === phaseData.phase ? 'rotate(180deg)' : 'rotate(0deg)',
            pointerEvents: 'none',
          }}
        >
          <ArrowDropDownIcon />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box className="phase-accordion-item__details-row">
          <Box className="phase-accordion-item__details-time-line">
            <Box
              className={clsx(
                'phase-accordion-item__collapsed-dashed-line',
                'phase-accordion-item__details-dashed-line',
                {
                  'phase-accordion-item__collapsed-dashed-line--future': futurePhase,
                }
              )}
            />
          </Box>
          <Box className="phase-accordion-item__details-content">
            {!futurePhase && (
              <Box>
                <EnergyFeedback
                  selectedFeedback={selectedFeedback}
                  onFeedbackSelect={handleFeedbackSelect}
                  disabled={false}
                  width="100%"
                />
              </Box>
            )}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PhaseAccordionItem;
